import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type {
  AgreementListQuery,
  ComplianceGrouping,
  ComplianceReport,
  CreateAgreement,
  CreateAssignment,
  CreateProcurementUnit,
  CreateVehicle,
  DuplicatePlate,
  FleetAssignment,
  FleetDataQuality,
  FleetSettings,
  FleetSummary,
  FrameworkAgreement,
  Page,
  ProcurementUnit,
  UpdateAgreement,
  UpdateAssignment,
  UpdateFleetSettings,
  UpdateProcurementUnit,
  UpdateVehicle,
  Vehicle,
  VehicleListQuery,
  VerifyAssignment,
} from '@dashboard/contracts';
import { apiRequest } from '../client';
import { useCompleteQuery } from '../pagination';
import { defaultQueryOptions } from './shared';

/**
 * The clean-vehicle fleet.
 *
 * Its own module rather than more of `index.ts`, following `commercial.ts` and
 * `documents.ts`. Everything here is organization-scoped, so no key carries a
 * project id -- which is the shape of the whole module and worth being able to
 * see at a glance.
 *
 * ## One invalidation rule
 *
 * A vehicle's facts are copied onto every live declaration when it is saved, so
 * a write anywhere in this module can move the compliance report, the data
 * quality counts and the summary at once. Rather than enumerate which write
 * moves which read -- and be wrong the first time somebody adds an endpoint --
 * every mutation invalidates the whole `['fleet']` prefix. The reads are small
 * and cached for minutes; the alternative is a stale regulatory figure, which
 * is the one thing this module must not show.
 */

export const fleetKeys = {
  all: ['fleet'] as const,
  settings: ['fleet', 'settings'] as const,
  summary: ['fleet', 'summary'] as const,
  dataQuality: ['fleet', 'data-quality'] as const,
  duplicatePlates: ['fleet', 'duplicate-plates'] as const,
  units: ['fleet', 'procurement-units'] as const,
  vehicles: (filters: Record<string, unknown> = {}) => ['fleet', 'vehicles', filters] as const,
  vehicle: (id: string) => ['fleet', 'vehicles', id] as const,
  agreements: (filters: Record<string, unknown> = {}) => ['fleet', 'agreements', filters] as const,
  assignments: (filters: Record<string, unknown> = {}) =>
    ['fleet', 'assignments', filters] as const,
  compliance: (period: string | undefined, groupBy: string | undefined) =>
    ['fleet', 'compliance', period ?? null, groupBy ?? null] as const,
};

function invalidateFleet(client: QueryClient): void {
  void client.invalidateQueries({ queryKey: fleetKeys.all });
}

/** Query-string values are only ever scalars here, and the parameter type says
 *  so: a filter object that grew a nested value would otherwise be serialized
 *  as "[object Object]" and silently ignored by the API. */
function toQuery(filters: Record<string, string | number | boolean | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  return search.size > 0 ? `?${search.toString()}` : '';
}

/* ------------------------------------------------------------- settings */

export function useFleetSettings() {
  return useQuery({
    ...defaultQueryOptions,
    queryKey: fleetKeys.settings,
    queryFn: ({ signal }) => apiRequest<FleetSettings>('/api/v1/fleet/settings', { signal }),
  });
}

export function useUpdateFleetSettings() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateFleetSettings) =>
      apiRequest<FleetSettings>('/api/v1/fleet/settings', { method: 'PATCH', body }),
    onSuccess: (saved) => {
      client.setQueryData(fleetKeys.settings, saved);
      /* The country decides which targets the report reads and the CPV list
         decides which agreements it counts, so every fleet figure on screen is
         downstream of this save. */
      invalidateFleet(client);
    },
  });
}

/* ------------------------------------------------------------- overview */

export function useFleetSummary(refetchInterval?: number, enabled = true) {
  return useQuery({
    ...defaultQueryOptions,
    queryKey: fleetKeys.summary,
    enabled,
    queryFn: ({ signal }) => apiRequest<FleetSummary>('/api/v1/fleet/summary', { signal }),
    refetchInterval,
  });
}

export function useFleetDataQuality(refetchInterval?: number) {
  return useQuery({
    ...defaultQueryOptions,
    queryKey: fleetKeys.dataQuality,
    queryFn: ({ signal }) => apiRequest<FleetDataQuality>('/api/v1/fleet/data-quality', { signal }),
    refetchInterval,
  });
}

export function useFleetDuplicatePlates(enabled = true) {
  return useQuery({
    ...defaultQueryOptions,
    queryKey: fleetKeys.duplicatePlates,
    queryFn: ({ signal }) =>
      apiRequest<DuplicatePlate[]>('/api/v1/fleet/duplicate-plates', { signal }),
    enabled,
  });
}

/**
 * The compliance report.
 *
 * `groupBy` is part of the key rather than a client-side transform: the three
 * groupings return different row *kinds* -- one carries targets and the other
 * two carry contributions -- and caching them as one would let a screen render
 * a target against a department, which is the one thing this module must never
 * do (contracts: fleet-compliance.ts).
 */
export function useFleetCompliance(
  query: { period?: string; groupBy?: ComplianceGrouping } = {},
  refetchInterval?: number,
) {
  return useQuery({
    ...defaultQueryOptions,
    queryKey: fleetKeys.compliance(query.period, query.groupBy),
    queryFn: ({ signal }) =>
      apiRequest<ComplianceReport>(`/api/v1/fleet/compliance${toQuery(query)}`, { signal }),
    refetchInterval,
  });
}

/* --------------------------------------------------- procurement units */

export function useProcurementUnits(enabled = true) {
  return useQuery({
    ...defaultQueryOptions,
    queryKey: fleetKeys.units,
    queryFn: ({ signal }) =>
      apiRequest<ProcurementUnit[]>('/api/v1/fleet/procurement-units', { signal }),
    enabled,
  });
}

export function useCreateProcurementUnit() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateProcurementUnit) =>
      apiRequest<ProcurementUnit>('/api/v1/fleet/procurement-units', { method: 'POST', body }),
    onSuccess: () => invalidateFleet(client),
  });
}

export function useUpdateProcurementUnit() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: UpdateProcurementUnit & { id: string }) =>
      apiRequest<ProcurementUnit>(`/api/v1/fleet/procurement-units/${id}`, {
        method: 'PATCH',
        body,
      }),
    onSuccess: () => invalidateFleet(client),
  });
}

export function useDeleteProcurementUnit() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<void>(`/api/v1/fleet/procurement-units/${id}`, { method: 'DELETE' }),
    onSuccess: () => invalidateFleet(client),
  });
}

/* ------------------------------------------------------------ vehicles */

/**
 * The whole register, fetched to completion.
 *
 * Exhaustive rather than one page with a Load more, and the reason is the
 * counts above the table rather than the table itself. Whether a vehicle is
 * clean depends on the date the reader picks, so it cannot be counted in SQL --
 * the screen has to judge the rows it holds. A partial list would therefore
 * produce partial counts presented as fleet totals, which is a wrong number
 * rather than a short list.
 *
 * The pages are bounded and indexed, so this costs a few round trips on a
 * register of hundreds and nothing on a register of ten. `isComplete` is what
 * the screen gates its counts on.
 */
export function useAllFleetVehicles(filters: VehicleListQuery = {}) {
  return useCompleteQuery<Vehicle>(fleetKeys.vehicles(filters), ({ signal, cursor }) =>
    apiRequest<Page<Vehicle>>(`/api/v1/fleet/vehicles${toQuery({ ...filters, cursor })}`, {
      signal,
    }),
  );
}

/** One page, for a picker or a tile that shows the top of a list. */
export function useFleetVehicles(filters: VehicleListQuery = {}) {
  return useQuery({
    ...defaultQueryOptions,
    queryKey: fleetKeys.vehicles(filters),
    queryFn: ({ signal }) =>
      apiRequest<Page<Vehicle>>(`/api/v1/fleet/vehicles${toQuery(filters)}`, { signal }),
  });
}

export function useFleetVehicle(id: string | undefined) {
  return useQuery({
    ...defaultQueryOptions,
    queryKey: fleetKeys.vehicle(id ?? ''),
    queryFn: ({ signal }) => apiRequest<Vehicle>(`/api/v1/fleet/vehicles/${id}`, { signal }),
    enabled: Boolean(id),
  });
}

export function useCreateVehicle() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateVehicle) =>
      apiRequest<Vehicle>('/api/v1/fleet/vehicles', { method: 'POST', body }),
    onSuccess: () => invalidateFleet(client),
  });
}

export function useUpdateVehicle() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: UpdateVehicle & { id: string }) =>
      apiRequest<Vehicle>(`/api/v1/fleet/vehicles/${id}`, { method: 'PATCH', body }),
    onSuccess: () => invalidateFleet(client),
  });
}

export function useDeleteVehicle() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<void>(`/api/v1/fleet/vehicles/${id}`, { method: 'DELETE' }),
    onSuccess: () => invalidateFleet(client),
  });
}

/** Confirms that vehicle records are current. An empty list means everything
 *  in service, which is what the button on the register does. */
export function useAttestVehicles() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (vehicleIds: string[]) =>
      apiRequest<{ attested: number }>('/api/v1/fleet/vehicles/attest', {
        method: 'POST',
        body: { vehicleIds },
      }),
    onSuccess: () => invalidateFleet(client),
  });
}

/* ---------------------------------------------------------- agreements */

/** Every agreement. Tens of rows, and the same argument as the vehicles: the
 *  reference-period filter is applied on the way out, so a partial list would
 *  drop rows that belong in the answer. */
export function useAllFleetAgreements(filters: AgreementListQuery = {}) {
  return useCompleteQuery<FrameworkAgreement>(fleetKeys.agreements(filters), ({ signal, cursor }) =>
    apiRequest<Page<FrameworkAgreement>>(
      `/api/v1/fleet/agreements${toQuery({ ...filters, cursor })}`,
      { signal },
    ),
  );
}

export function useFleetAgreements(filters: AgreementListQuery = {}, refetchInterval?: number) {
  return useQuery({
    ...defaultQueryOptions,
    queryKey: fleetKeys.agreements(filters),
    queryFn: ({ signal }) =>
      apiRequest<Page<FrameworkAgreement>>(`/api/v1/fleet/agreements${toQuery(filters)}`, {
        signal,
      }),
    refetchInterval,
  });
}

export function useCreateAgreement() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateAgreement) =>
      apiRequest<FrameworkAgreement>('/api/v1/fleet/agreements', { method: 'POST', body }),
    onSuccess: () => invalidateFleet(client),
  });
}

export function useUpdateAgreement() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: UpdateAgreement & { id: string }) =>
      apiRequest<FrameworkAgreement>(`/api/v1/fleet/agreements/${id}`, { method: 'PATCH', body }),
    onSuccess: () => invalidateFleet(client),
  });
}

export function useDeleteAgreement() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<void>(`/api/v1/fleet/agreements/${id}`, { method: 'DELETE' }),
    onSuccess: () => invalidateFleet(client),
  });
}

/* --------------------------------------------------------- declarations */

/** Every declaration on one agreement. */
export function useAllFleetAssignments(filters: AssignmentFilters = {}) {
  return useCompleteQuery<FleetAssignment>(fleetKeys.assignments(filters), ({ signal, cursor }) =>
    apiRequest<Page<FleetAssignment>>(
      `/api/v1/fleet/assignments${toQuery({ ...filters, cursor })}`,
      { signal },
    ),
  );
}

export function useFleetAssignments(filters: AssignmentFilters = {}, enabled = true) {
  return useQuery({
    ...defaultQueryOptions,
    queryKey: fleetKeys.assignments(filters),
    queryFn: ({ signal }) =>
      apiRequest<Page<FleetAssignment>>(`/api/v1/fleet/assignments${toQuery(filters)}`, { signal }),
    enabled,
  });
}

type AssignmentFilters = {
  agreementId?: string;
  vehicleId?: string;
  verification?: string;
  includeWithdrawn?: boolean;
  limit?: number;
};

export function useCreateAssignment() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateAssignment) =>
      apiRequest<FleetAssignment>('/api/v1/fleet/assignments', { method: 'POST', body }),
    onSuccess: () => invalidateFleet(client),
  });
}

export function useUpdateAssignment() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: UpdateAssignment & { id: string }) =>
      apiRequest<FleetAssignment>(`/api/v1/fleet/assignments/${id}`, { method: 'PATCH', body }),
    onSuccess: () => invalidateFleet(client),
  });
}

/**
 * Takes a declaration back.
 *
 * A POST to a named action, not a DELETE, because it is not one: the row
 * survives and keeps counting in the audit trail. Naming it `withdraw` in the
 * hook too keeps the call site honest about what it does.
 */
export function useWithdrawAssignment() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<FleetAssignment>(`/api/v1/fleet/assignments/${id}/withdraw`, { method: 'POST' }),
    onSuccess: () => invalidateFleet(client),
  });
}

export function useVerifyAssignment() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: VerifyAssignment & { id: string }) =>
      apiRequest<FleetAssignment>(`/api/v1/fleet/assignments/${id}/verify`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => invalidateFleet(client),
  });
}
